import _en from './translations/en.json.mjs';
import React__default from 'react';
import { useI18n } from '@shopify/react-i18n';
import classNames from 'classnames';
import { ChevronLeftIcon, XIcon } from '@shopify/polaris-icons';
import styles from './Header.scss.mjs';
import { hasSectionsWithItems, RollupMenu } from '../../../RollupMenu/RollupMenu.mjs';
import { PlainAction } from '../../../PlainAction/PlainAction.mjs';

// Required above Header due to a Babel hoisting issue
// https://github.com/babel/babel/issues/10878
function Title({
  title,
  strong = false
}) {
  const titleHeadingClassNames = classNames(styles.TitleHeading, {
    [styles.strong]: strong
  });
  const titleTextMarkup = /*#__PURE__*/React__default.createElement("span", {
    className: titleHeadingClassNames
  }, title);
  return /*#__PURE__*/React__default.createElement("div", {
    className: styles.Title
  }, titleTextMarkup);
}
function LeftAction({
  children
}) {
  return children;
}
function RightAction({
  children
}) {
  return children;
}
function CloseAction({
  closeText,
  canRenderBackArrow,
  accessibilityLabel,
  onClose
}) {
  if (!onClose) {
    return null;
  }
  const canRenderCloseText = closeText !== undefined && !canRenderBackArrow;
  const useCloseText = Boolean(canRenderCloseText);
  const onCloseIcon = getOnCloseIcon({
    backArrow: canRenderBackArrow,
    closeText: canRenderCloseText,
    onClose: Boolean(onClose)
  });
  return /*#__PURE__*/React__default.createElement("div", {
    className: styles.CloseAction
  }, /*#__PURE__*/React__default.createElement(PlainAction, {
    icon: onCloseIcon,
    content: useCloseText ? closeText : undefined,
    unstyled: useCloseText,
    accessibilityLabel: useCloseText ? undefined : accessibilityLabel,
    onAction: onClose
  }));
}
function Header({
  id,
  children,
  rollupMenu,
  backArrow = false,
  closeText,
  onClose,
  ...contentProps
}) {
  const [i18n] = useI18n({
    id: 'Header_qtpo1r',
    fallback: _en,
    translations(locale) {
      if (["cs", "da", "de", "es", "fi", "fr", "it", "ja", "ko", "nb", "nl", "pl", "pt-BR", "pt-PT", "sv", "th", "tr", "vi", "zh-CN", "zh-TW"].indexOf(locale) < 0) {
        return;
      }
      return import( /* webpackChunkName: "Header_qtpo1r-i18n", webpackMode: "lazy-once" */`./translations/${locale}.json`).then(dict => dict && dict.default);
    }
  });
  const closeActionAccessibilityLabel = i18n.translate('PanelCard.Header.CloseAction.accessibilityLabel', {
    title: contentProps.title
  });
  const viewActionsAccessibilityLabel = i18n.translate('PanelCard.Header.RollupMenu.accessibilityLabel', {
    title: contentProps.title
  });
  const rollupMenuMarkup = rollupMenu && hasSectionsWithItems(rollupMenu.sections) ? /*#__PURE__*/React__default.createElement("div", {
    className: styles.RollupMenuWrapper
  }, /*#__PURE__*/React__default.createElement(RollupMenu, Object.assign({}, rollupMenu, {
    accessibilityLabel: viewActionsAccessibilityLabel
  }))) : null;
  const canRenderBackArrow = Boolean(backArrow || rollupMenuMarkup);
  const useBackArrowIcon = Boolean(onClose && canRenderBackArrow);
  const useRightCloseAction = Boolean(onClose && !canRenderBackArrow);
  const closeActionMarkup = /*#__PURE__*/React__default.createElement(CloseAction, {
    closeText: closeText,
    canRenderBackArrow: canRenderBackArrow,
    accessibilityLabel: closeActionAccessibilityLabel,
    onClose: onClose
  });
  const headerClassNames = classNames(styles.Header, {
    [styles.hasRollupMenu]: rollupMenuMarkup,
    [styles.hasBackArrow]: useBackArrowIcon,
    [styles.hasRightCloseAction]: useRightCloseAction
  });
  return /*#__PURE__*/React__default.createElement("header", {
    className: headerClassNames,
    id: id
  }, children, /*#__PURE__*/React__default.createElement(LeftAction, null, useRightCloseAction ? null : closeActionMarkup), /*#__PURE__*/React__default.createElement(Content, contentProps), /*#__PURE__*/React__default.createElement(RightAction, null, useRightCloseAction ? closeActionMarkup : rollupMenuMarkup));
}
function Content({
  prefix,
  subtitle,
  ...titleProps
}) {
  const prefixMarkup = prefix ? /*#__PURE__*/React__default.createElement("div", {
    className: styles.Prefix
  }, prefix) : null;

  // TODO: We might want to use <Truncate /> if `subtitle` is a `string`.
  const subtitleMarkup = subtitle ? /*#__PURE__*/React__default.createElement("div", {
    className: styles.Subtitle
  }, subtitle) : null;
  return /*#__PURE__*/React__default.createElement("div", {
    className: styles.Content
  }, /*#__PURE__*/React__default.createElement("div", {
    className: styles.PrefixTitleWrapper
  }, prefixMarkup, /*#__PURE__*/React__default.createElement(Title, titleProps)), subtitleMarkup);
}
function getOnCloseIcon({
  backArrow,
  closeText,
  onClose
}) {
  if (!onClose || closeText) {
    return undefined;
  }
  return backArrow ? ChevronLeftIcon : XIcon;
}

export { CloseAction, Header, LeftAction, RightAction };
